/**
 * Building identity: names, use, floors, hours and access.
 *
 * Denver's footprint data has geometry and height but no name and no use.
 * OpenStreetMap has all four, so the two are matched here by proximity — the
 * server returns each named building's centroid, and a hovered footprint
 * claims the nearest one within a short radius.
 *
 * Proximity rather than a spatial join because the two datasets disagree about
 * where a building's edges are: OSM traces from imagery, Denver from survey.
 * A centroid within ~45m is the same building in practice, and a miss simply
 * means the tooltip falls back to geometry, which is still true.
 */

/** Metres per degree at Denver's latitude. */
const M_PER_DEG_LNG = 85600;
const M_PER_DEG_LAT = 110540;

/** How far a footprint will reach for a name. */
const MATCH_RADIUS_M = 45;

/** Grid cell for the lookup index, in degrees (~110m). */
const CELL = 0.001;

/** OSM `building=*` values, rendered as something a person would say. */
const USE_LABELS = {
  apartments: "Apartments", residential: "Residential", house: "House",
  detached: "House", terrace: "Terraced housing", dormitory: "Dormitory",
  hotel: "Hotel", commercial: "Commercial", office: "Office", retail: "Retail",
  supermarket: "Supermarket", industrial: "Industrial", warehouse: "Warehouse",
  civic: "Civic", public: "Public building", government: "Government",
  school: "School", university: "University", college: "College",
  hospital: "Hospital", church: "Church", cathedral: "Cathedral",
  chapel: "Chapel", synagogue: "Synagogue", mosque: "Mosque",
  train_station: "Transit station", parking: "Parking structure",
  garage: "Garage", garages: "Garages", service: "Service building",
  roof: "Canopy", construction: "Under construction", yes: "Unspecified",
};

/**
 * OSM `access=*`, described in terms of who can get in.
 *
 * This is the closest thing to a security posture that public data carries.
 * It is a right-of-way tag, not a security audit, so the wording says who is
 * permitted rather than implying anything about locks or guards.
 */
const ACCESS_LABELS = {
  yes: "Open to the public",
  public: "Open to the public",
  permissive: "Open, at the owner's discretion",
  customers: "Customers only",
  destination: "Visitors with business here",
  permit: "Permit holders only",
  private: "Private — no public access",
  no: "Closed to the public",
};

export class PlaceIndex {
  constructor() {
    this.places = [];
    this.grid = new Map();
    this.status = "idle";
  }

  static cellKey(lng, lat) {
    return `${Math.round(lng / CELL)}:${Math.round(lat / CELL)}`;
  }

  async load() {
    try {
      // Named buildings are baked citywide at build time (scripts/bake.mjs),
      // not the downtown-only /api/buildings: 1,656 -> 12,157. Still streamed in
      // after first paint, so the wider set is background bytes, not startup.
      const response = await fetch("./data/building-names.json");
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this.places = body.buildings ?? [];
      this.index();
      this.status = this.places.length ? "ready" : "empty";
    } catch (error) {
      // Names are an enhancement; geometry still works without them.
      this.status = "unavailable";
      console.warn("place names unavailable:", error.message);
    }
    return this.places.length;
  }

  index() {
    this.grid.clear();
    for (const place of this.places) {
      const key = PlaceIndex.cellKey(place.lng, place.lat);
      if (!this.grid.has(key)) this.grid.set(key, []);
      this.grid.get(key).push(place);
    }
  }

  /** Nearest named building to a coordinate, or null. */
  nearest([lng, lat]) {
    if (!this.places.length) return null;

    let best = null;
    let bestDistance = Infinity;
    const cx = Math.round(lng / CELL);
    const cy = Math.round(lat / CELL);

    // Search the 3x3 neighbourhood: the match radius is smaller than a cell,
    // so a candidate can only be in an adjacent one.
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (const place of this.grid.get(`${cx + dx}:${cy + dy}`) ?? []) {
          const mx = (place.lng - lng) * M_PER_DEG_LNG;
          const my = (place.lat - lat) * M_PER_DEG_LAT;
          const distance = Math.hypot(mx, my);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = place;
          }
        }
      }
    }

    return bestDistance <= MATCH_RADIUS_M ? best : null;
  }
}

export function useLabel(value) {
  if (!value) return null;
  return USE_LABELS[value] ?? value.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function accessLabel(value) {
  if (!value) return null;
  return ACCESS_LABELS[value] ?? value;
}

/**
 * Floors, preferring OSM's surveyed count over an estimate from height.
 *
 * The estimate was wrong in a way worth naming: dividing height by 3.6m
 * over-counts, because Denver's height field includes parapets and rooftop
 * plant, and commercial floor-to-floor is nearer 4m than 3.6m. So a real
 * `building:levels` always wins, and the estimate is labelled as one.
 */
export function describeFloors(metres, place) {
  if (place?.levels) {
    const below = place.levels_below;
    return {
      value: below ? `${place.levels} above ground, ${below} below` : `${place.levels}`,
      exact: true,
    };
  }
  // ~4m floor-to-floor, then drop one for roof structure the height includes.
  const estimate = Math.max(1, Math.round(metres / 4.0));
  return { value: `~${estimate} (estimated)`, exact: false };
}
